import { Component, type ReactElement } from "react";
import { connect } from "react-redux";
import { change, type InjectedFormProps, reduxForm } from "redux-form";
import { Callout, CalloutIntent } from "widgets";

import { ATTRIBUTE_ID, ATTRIBUTE_TYPE, MAX_LENGTH } from "@/settings/settings.js";

import { trimOrThrow } from "../../app-utils/string.js";
import { freeTextValidation, numberValidation, whiteSpaceValidation } from "../../app-utils/validation.js";
import {
  type Attribute,
  type InputAttributeDefaultInput,
  type RawAttributeDefault,
  type VocabularyTerm,
} from "../../graphql.js";
import { getAttributeLabel, isDefinedNumber } from "../../redux/modules/sample-admin.js";
import { type IAppState } from "../../redux/reducer.js";
import { SCRIPTING_MACRO } from "../core/forms/ScriptInputField.js";
import { Dialog } from "../core/overlays/Dialogs.js";
import InputField, { InputReduxFormField } from "../core/redux-form/InputField.js";
import ReactSelectField, { ReactSelectReduxFormField } from "../core/redux-form/ReactSelectField.js";
import { ScriptInputReduxFormField } from "../core/redux-form/ScriptInputField.js";
import { type IAttributeWithDefault } from "./AddSampleTypeDialog.js";

interface IConfigureSampleTypeAttributeDialogOwnProps {
  onApply?: (attributeId: number, configurationPayload: InputAttributeDefaultInput) => void;
  onCancel?: () => void;

  templateAttributes: Attribute[];
  attribute: IAttributeWithDefault;
}

interface IConfigureSampleTypeAttributeDialogAppStateProps {
  initialValues: Partial<FormData>;
}

interface IConfigureSampleTypeAttributeDialogDispatchProps {
  changeFieldValue: (fieldName, value) => void;
}

export interface IConfigureSampleTypeAttributeDialogProps
  extends IConfigureSampleTypeAttributeDialogAppStateProps,
    IConfigureSampleTypeAttributeDialogDispatchProps,
    IConfigureSampleTypeAttributeDialogOwnProps,
    InjectedFormProps<FormData, any> {}

function mapStateToProps(
  _state: IAppState,
  ownProps: IConfigureSampleTypeAttributeDialogOwnProps,
): IConfigureSampleTypeAttributeDialogAppStateProps {
  const defaults = ownProps.attribute.attributeDefault;
  return {
    initialValues: {
      [ConfigureSampleTypeAttributeDialogImpl.CONFIGURE_TYPE_ATTRIBUTE_DEFAULT_NUMBER]:
        defaults && isDefinedNumber(defaults.defaultNumber) ? String(defaults.defaultNumber) : null,
      [ConfigureSampleTypeAttributeDialogImpl.CONFIGURE_TYPE_ATTRIBUTE_DEFAULT_TEXT]: defaults?.defaultText,
      [ConfigureSampleTypeAttributeDialogImpl.CONFIGURE_TYPE_ATTRIBUTE_DEFAULT_VOC_TERM]: defaults?.defaultTerm,
      [ConfigureSampleTypeAttributeDialogImpl.CONFIGURE_TYPE_ATTRIBUTE_TEMPLATE]: defaults?.template,
    },
  };
}

function mapDispatchToProps(dispatch): IConfigureSampleTypeAttributeDialogDispatchProps {
  return {
    changeFieldValue: (fielName, value) =>
      dispatch(change(ConfigureSampleTypeAttributeDialogImpl.CONFIGURE_TYPE_ATTRIBUTE_FORM_NAME, fielName, value)),
  };
}

class ConfigureSampleTypeAttributeDialogImpl extends Component<IConfigureSampleTypeAttributeDialogProps> {
  public static CONFIGURE_TYPE_ATTRIBUTE_FORM_NAME = "CONFIGURE_SAMPLE_TYPE_ATTRIBUTE_FORM";
  public static CONFIGURE_TYPE_ATTRIBUTE_DEFAULT_NUMBER = "defaultNumber";
  public static CONFIGURE_TYPE_ATTRIBUTE_DEFAULT_TEXT = "defaultText";
  public static CONFIGURE_TYPE_ATTRIBUTE_DEFAULT_VOC_TERM = "defaultVocTerm";
  public static CONFIGURE_TYPE_ATTRIBUTE_TEMPLATE = "template";

  constructor(props: IConfigureSampleTypeAttributeDialogProps) {
    super(props);
  }

  handleSubmitData = (values): Promise<any> => {
    const numberDefault = values[ConfigureSampleTypeAttributeDialogImpl.CONFIGURE_TYPE_ATTRIBUTE_DEFAULT_NUMBER];
    const configurationPayload: Partial<RawAttributeDefault> = {
      // if not set, the numberDefault can either be an empty string or null!
      defaultNumber: !numberDefault || numberDefault.length === 0 ? null : Number(numberDefault),
      defaultText: trimOrThrow(values[ConfigureSampleTypeAttributeDialogImpl.CONFIGURE_TYPE_ATTRIBUTE_DEFAULT_TEXT]),
      defaultTerm: values[ConfigureSampleTypeAttributeDialogImpl.CONFIGURE_TYPE_ATTRIBUTE_DEFAULT_VOC_TERM],
      template: trimOrThrow(values[ConfigureSampleTypeAttributeDialogImpl.CONFIGURE_TYPE_ATTRIBUTE_TEMPLATE]),
    };

    this.props.onApply(this.props.attribute.id, configurationPayload);
    return Promise.resolve(true);
  };

  render(): ReactElement {
    return (
      <Dialog
        headerProps={{ title: `Configure Attribute` }}
        mainActionButtonProps={{
          children: `Apply`,
          isDisabled: this.props.submitting || this.props.invalid,
          onPress: this.props.handleSubmit(this.handleSubmitData),
        }}
        closeFn={this.props.onCancel}
      >
        <div>
          <p>
            You can add some specific configuration for attribute {getAttributeLabel(this.props.attribute)} when used in
            the context of this sample type.
          </p>
          {this.props.attribute.id === ATTRIBUTE_ID.SAMPLE_NAME ? (
            <p>If you decide not to specify a template, the sample names will be defaulted to a standard.</p>
          ) : null}
          {this.props.attribute.id !== ATTRIBUTE_ID.SAMPLE_NAME &&
            this.props.attribute.attributeType.id === ATTRIBUTE_TYPE.FREE_TEXT && (
              <InputReduxFormField
                componentClass="input"
                name={ConfigureSampleTypeAttributeDialogImpl.CONFIGURE_TYPE_ATTRIBUTE_DEFAULT_TEXT}
                label={"Default text"}
                placeholder={`Enter default text`}
                // maxLength can not be 0
                maxLength={this.props.attribute.maxLength || MAX_LENGTH.ATTRIBUTE.DEFAULT_TEXT}
                component={InputField}
                validate={this.validateFreeText}
              />
            )}

          {this.props.attribute.attributeType.id === ATTRIBUTE_TYPE.FREE_TEXT && (
            <ScriptInputReduxFormField
              name={ConfigureSampleTypeAttributeDialogImpl.CONFIGURE_TYPE_ATTRIBUTE_TEMPLATE}
              title={"Template"}
              maxLength={MAX_LENGTH.ATTRIBUTE.TEMPLATE}
              attributes={this.props.templateAttributes}
              acceptedMacros={[SCRIPTING_MACRO.DATE, SCRIPTING_MACRO.PARENT_TYPE]}
            />
          )}

          {this.props.attribute.attributeType.id === ATTRIBUTE_TYPE.FREE_NUMERICAL && (
            <InputReduxFormField
              componentClass="input"
              type="number"
              name={ConfigureSampleTypeAttributeDialogImpl.CONFIGURE_TYPE_ATTRIBUTE_DEFAULT_NUMBER}
              label={"Default number"}
              placeholder={`Enter default number`}
              component={InputField}
              validate={this.validateNumber}
            />
          )}

          {this.props.attribute.attributeType.id === ATTRIBUTE_TYPE.CONTROLLED_TEXT && (
            <ReactSelectReduxFormField
              name={ConfigureSampleTypeAttributeDialogImpl.CONFIGURE_TYPE_ATTRIBUTE_DEFAULT_VOC_TERM}
              label={"Default term"}
              placeholder="Select a default..."
              component={ReactSelectField}
              options={this.props.attribute.vocabularyTerms}
              isSearchable
              closeMenuOnSelect
              openMenuOnFocus
              isClearable={true}
              isMulti={false}
              getOptionValue={(o: VocabularyTerm | null) => o && o.id}
              getOptionLabel={(o: VocabularyTerm | null) => o && o.name}
            />
          )}

          {this.props.error && <Callout intent={CalloutIntent.Danger}>{this.props.error}</Callout>}
        </div>
      </Dialog>
    );
  }

  validateNumber = (value: string): string => {
    if (!value || value.trim().length === 0) {
      return undefined;
    }
    return numberValidation(value, this.props.attribute);
  };

  validateFreeText = (value: string): string => {
    if (!value || value.trim().length === 0) {
      return undefined;
    }
    return whiteSpaceValidation(value) || freeTextValidation(value, this.props.attribute);
  };

  static validate = (values): { _error?: string } => {
    const defaultText: string = values[ConfigureSampleTypeAttributeDialogImpl.CONFIGURE_TYPE_ATTRIBUTE_DEFAULT_TEXT];
    const template: string = values[ConfigureSampleTypeAttributeDialogImpl.CONFIGURE_TYPE_ATTRIBUTE_TEMPLATE];

    if (!!defaultText && !!template) {
      return { _error: "You can fill only one of default text or template input fields." };
    }
    return {};
  };
}

const ConfigureSampleTypeAttributeDialog = connect<
  IConfigureSampleTypeAttributeDialogAppStateProps,
  IConfigureSampleTypeAttributeDialogDispatchProps,
  IConfigureSampleTypeAttributeDialogOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    form: ConfigureSampleTypeAttributeDialogImpl.CONFIGURE_TYPE_ATTRIBUTE_FORM_NAME,
    validate: ConfigureSampleTypeAttributeDialogImpl.validate, // we use field level validation
  })(ConfigureSampleTypeAttributeDialogImpl) as any,
);

export { ConfigureSampleTypeAttributeDialog, ConfigureSampleTypeAttributeDialogImpl };
export default ConfigureSampleTypeAttributeDialog;
